// Canonical advance-pay payers (the `advancePay` values on billing lines) and
// the TYPE each rolls up to on the รายการตามเบิก (reimbursement) summary. These
// types reconcile very differently — government claims vs insurance direct-claim
// vs marketplace/partner payouts vs corporate subsidy vs doctor/internal — so the
// summary groups by them.
//
// EDIT ME: to re-file a payer, move its name to another group's `payers` array.
// Matching is tolerant (case-insensitive, ignores spaces/dots/dashes), so
// "สปสช." matches "สปสช" and "ดี-aligner" matches "ดี aligner". Any payer NOT
// listed here falls into the LAST group (หมอ / อื่นๆ) so nothing is ever dropped
// — which is why `other` has to stay last in this array.

export const PAYER_GROUPS = [
  { key: 'gov',       label: 'ภาครัฐ',                color: '#0d9488', payers: ['สปสช.', 'ปกส.'] },
  { key: 'insurance', label: 'ประกัน',                color: '#6366f1', payers: ['AIA', 'AXA', 'Prudential', 'Generali', 'อาคเนย์', 'Cynergy'] },
  { key: 'platform',  label: 'แพลตฟอร์ม/พาร์ทเนอร์',   color: '#d97706', payers: ['HDs', 'Gowabi', 'Before dent', 'Full Smile', 'ดี-aligner'] },
  // CORPORATE SUBSIDY. A company Tooth Taxi visits agrees to cover part or all of
  // an employee's treatment, and settles with the clinic afterwards — the line is
  // billed on the day and the money is chased later, which is exactly what ตามเบิก
  // is for. Its own bucket because chasing an employer's HR/บัญชี reconciles like
  // nothing else here: no claim portal, no policy number, just an invoice and a
  // phone call. ONE payer for EVERY company, deliberately — see EXTRA_PAYERS.
  { key: 'company',   label: 'บริษัท (Tooth Taxi)',    color: '#2563eb', payers: ['Tooth Taxi'] },
  { key: 'other',     label: 'หมอ / อื่นๆ',           color: '#64748b', payers: ['หมอเอ็ม', 'หมอแป้ง', 'Recurring'] },
]

// Payers the billing_type catalogue does not name. The ผู้จ่าย picker on a billing
// line is built from the distinct `billing_type.advancePay` values, so a payer no
// treatment type carries would otherwise be unpickable. A company subsidy is billed
// on ORDINARY treatment types — the payer is the only thing that differs — so
// nothing in the catalogue names it, and adding a fake catalogue row to create the
// option would put a fake treatment in the type picker.
//
// "Tooth Taxi" is the single umbrella for every company on purpose (owner's call,
// 2026-08-24): one payer to chase and one row on the ตามเบิก summary, instead of
// the payer list sprouting an entry per employer. Which company owes goes in the
// line's รายละเอียด / รหัสเคลม, not in a new payer.
//
// "อาคเนย์" (Ram, 2026-09-19) — an insurer no catalogue row names yet. Spelled in
// Thai exactly as Ram wrote it: whatever is saved here IS the key every ตามเบิก
// report and the Recall tag match on, so do not respell it once lines carry it.
export const EXTRA_PAYERS = ['Tooth Taxi', 'อาคเนย์']

// How the ผู้จ่าย picker on a billing line is SECTIONED (Ram, 2026-09-19). Not
// PAYER_GROUPS: that one is the ตามเบิก report's reconciliation buckets (it keeps
// a company bucket for Tooth Taxi, and หมอ / อื่นๆ); this is just what staff scan
// down when picking. Options follow the order listed here; any payer not listed
// lands in the last section, อื่นๆ, sorted — so a new catalogue payer is never
// hidden, only unsectioned.
export const PICKER_GROUPS = [
  { label: 'หน่วยงานรัฐ', payers: ['ปกส.', 'สปสช.'] },
  { label: 'ประกัน',     payers: ['AIA', 'AXA', 'Generali', 'Prudential', 'อาคเนย์'] },
  { label: 'Partner',    payers: ['HDs', 'Gowabi', 'Full Smile', 'Before dent'] },
]
const PICKER_OTHER = 'อื่นๆ'

const OTHER_KEY = 'other'
/** Tolerant payer key — case-insensitive, ignores spaces/dots/dashes. */
export const normPayer = (s) => String(s ?? '').toLowerCase().replace(/[\s.\-]/g, '')
const norm = normPayer

// normalized payer → group key
const LOOKUP = (() => {
  const m = new Map()
  for (const g of PAYER_GROUPS) for (const p of g.payers) m.set(norm(p), g.key)
  return m
})()

export function payerGroupKey(payerName) {
  return LOOKUP.get(norm(payerName)) ?? OTHER_KEY
}

// normalized payer → [section index, position within it]
const PICKER_LOOKUP = (() => {
  const m = new Map()
  PICKER_GROUPS.forEach((g, gi) => g.payers.forEach((p, pi) => m.set(norm(p), [gi, pi])))
  return m
})()

/** Payer strings → picker options `{ value, label, group }`, in section order. */
export function payerPickerOptions(payers) {
  const at = (p) => PICKER_LOOKUP.get(norm(p)) ?? [PICKER_GROUPS.length, 0]
  return [...payers]
    .sort((a, b) => {
      const [ga, pa] = at(a), [gb, pb] = at(b)
      return ga - gb || pa - pb || a.localeCompare(b, 'th')
    })
    .map((p) => ({ value: p, label: p, group: PICKER_GROUPS[at(p)[0]]?.label ?? PICKER_OTHER }))
}

export function payerGroupMeta(key) {
  return PAYER_GROUPS.find((g) => g.key === key) ?? PAYER_GROUPS[PAYER_GROUPS.length - 1]
}
